import { Mail, Phone, Briefcase } from 'lucide-react';
import {
  ONBOARDING_STATUS_LABELS,
  type OnboardingCase,
  type OnboardingStatus,
} from '@/features/user-management/onboarding';

interface Props {
  candidate: OnboardingCase;
  liveState?: string;
}

const STATUS_BADGE_CLASS: Partial<Record<OnboardingStatus, string>> = {
  onboarding_start: 'badge-info',
  review_phase: 'badge-warning',
  rejected: 'badge-error',
  onboarding_complete: 'badge-success',
};

/** Title-case an unknown state like `candidate_document_upload` → `Candidate Document Upload`. */
const humanizeState = (state: string): string =>
  state
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const labelForState = (state: string): string =>
  ONBOARDING_STATUS_LABELS[state as OnboardingStatus] ?? humanizeState(state);

const badgeForState = (state: string): string =>
  STATUS_BADGE_CLASS[state as OnboardingStatus] ?? 'badge-info';

export const CandidateProfileCard = ({ candidate, liveState }: Props) => {
  const state = liveState || candidate.state;
  const job = candidate.job;

  return (
    <div className="card">
      <div className="card-body space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-rgf-gray-500 uppercase tracking-wide">Hello</p>
            <h2 className="text-lg sm:text-xl font-semibold text-rgf-gray-900 truncate">
              {candidate.contact?.name || 'Candidate'}
            </h2>
          </div>
          <span
            className={`badge ${badgeForState(state)} self-start whitespace-normal break-words max-w-[60%] text-center leading-snug`}
          >
            {labelForState(state)}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {candidate.contact?.email && (
            <div className="flex items-start gap-2 text-rgf-gray-700 min-w-0">
              <Mail className="h-4 w-4 mt-0.5 text-rgf-gray-400 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate" title={candidate.contact.email}>
                {candidate.contact.email}
              </span>
            </div>
          )}
          {candidate.contact?.phone_number && (
            <div className="flex items-start gap-2 text-rgf-gray-700 min-w-0">
              <Phone className="h-4 w-4 mt-0.5 text-rgf-gray-400 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">{candidate.contact.phone_number}</span>
            </div>
          )}
          {job?.title && (
            <div className="flex items-start gap-2 text-rgf-gray-700 sm:col-span-2 min-w-0">
              <Briefcase className="h-4 w-4 mt-0.5 text-rgf-gray-400 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                {job.title}
                {job.program ? ` · ${job.program}` : ''}
                {job.department ? ` · ${job.department}` : ''}
              </span>
            </div>
          )}
        </div>

        {candidate.rejection_reason && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <div className="font-medium mb-1">Application not progressed</div>
            <div className="break-words">{candidate.rejection_reason}</div>
          </div>
        )}
      </div>
    </div>
  );
};
